"use client";
// CareerEventsV2, redesign of /career-events (career-events.html mockup).
// The mockup is a deliberately simplified static vision; this v2 keeps the FULL v1
// feature set and applies the mockup's visual language (topbar eyebrow/title, token
// cards, date badges, type tags, tints). Surfaces the mockup omits (search, filters,
// detail panel, save, tips) are rebuilt in the same token style, zero feature loss.
//
// PARITY with v1 (every visible feature preserved):
//  - profile prefill (location / career level / target role) from local storage
//  - cached real events fetch (/api/career-events/cached); live search
//    (/api/career-events/search) with error banner
//  - display priority: live search > cached DB events > sample fallback
//  - hero/topbar with live stats (events count · DACH · saved count)
//  - event-type filter (select + desktop pills); save toggle (set + count)
//  - event cards grid (responsive; reflows when a detail is open)
//  - detail panel (desktop right column + mobile bottom-sheet modal): key-info grid,
//    description, AI relevance, highlights, companies, audience+industries, CTA
//  - tips section · full DE/EN i18n
//
// Debt paid vs v1: removed the BANNED Sparkles icon (AI relevance → Star, tips → Lightbulb);
//  dropped the 6 raw Tailwind rainbow gradients for token-based per-type tints; inline
//  inputs/select/buttons → @/design primitives (labels now above, per DESIGN.md);
//  raw localStorage → storage wrapper; empty `.catch(() => {})` → logger.

import { T, useTextLocale, tl } from "@/components/t";
import { useState, useEffect } from "react";
import {
  Calendar, MapPin, Search, Star, Clock, CheckCircle,
  X, ExternalLink, Bookmark, Tag as TagIcon, Lightbulb, ChevronLeft,
} from "lucide-react";
import { Button, Input, Select, Icon, EmptyState } from "@/design";
import { storage } from "@/lib/storage";
import { logger } from "@/lib/logger";
import {
  TYPE_CONFIG, EVENT_TIPS, formatDateShort, eventIsUpcoming,
  type CareerEvent, type SearchResult,
  EN_COPY as EVENT_COPY_EN,
} from "./eventsData";
import styles from "./CareerEventsV2.module.css";

const API = process.env.NEXT_PUBLIC_API_URL || "";
const PROFILE_KEY = "careerkit_user_profile";

interface UserProfile { location?: string; career_level?: string; career_goal?: string }
const SAVED_KEY = "career_events_saved";
// The career-level filter matches the audience the organiser names; events that name
// none stay visible (unknown is not "not for you").
const LEVEL_AUDIENCE: Record<string, RegExp> = {
  pupil: /schül/i, student: /stud/i, entry: /absolvent|einsteig|berufsstart|azubi|ausbildung/i,
  professional: /berufserfahr|fachkr|professional|führungs|young professional/i,
};
const WEEKDAYS = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];
function daysUntil(dateStr: string): { days: number; weekday: string } | null {
  const [d, m, y] = (dateStr || "").split(".").map((x) => parseInt(x, 10));
  if (!d || !m || !y) return null;
  const ev = new Date(y, m - 1, d); const today = new Date(); today.setHours(0, 0, 0, 0);
  return { days: Math.round((ev.getTime() - today.getTime()) / 86400000), weekday: WEEKDAYS[ev.getDay()] };
}

/* ─── Event Card ──────────────────────────────────────────────────────────── */

function EventCard({ event, saved, onToggleSave, onSelect, isSelected }: {
  event: CareerEvent; saved: boolean; onToggleSave: () => void;
  onSelect: () => void; isSelected: boolean;
}) {
  const loc = useTextLocale();
  const cfg = TYPE_CONFIG[event.type] || TYPE_CONFIG.karrieremesse;
  const { day, month } = formatDateShort(event.date);

  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(); } }}
      aria-pressed={isSelected}
      className={styles.card}
      data-selected={isSelected || undefined}
    >
      {/* Tinted header (token tone per type) */}
      <div className={styles.cardHead} data-tone={cfg.tone}>
        <div className={styles.cardHeadTop}>
          <span className={styles.typeBadge}>
            <Icon icon={cfg.icon} size={13} decorative /> {tl(loc, cfg.label, cfg.labelEn)}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleSave(); }}
            className={styles.saveBtn}
            data-saved={saved || undefined}
            aria-pressed={saved}
            aria-label={saved ? tl(loc, "Gemerkt", "Saved") : tl(loc, "Merken", "Save")}
          >
            <Icon icon={Bookmark} size={16} decorative className={saved ? styles.bookmarkFilled : undefined} />
          </button>
        </div>
        <div className={styles.cardHeadBottom}>
          <div className={styles.dateBadge}>
            <span className={styles.dateDay}>{day}</span>
            <span className={styles.dateMonth}>{month}</span>
          </div>
          <div className={styles.cardHeadText}>
            <h3 className={styles.cardName}>{event.name}</h3>
            <p className={styles.cardOrg}>{event.organizer}</p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        <div className={styles.metaRow}>
          <span className={styles.meta}><Icon icon={MapPin} size={15} decorative /> {event.location?.city}{event.format === "Online" ? " (Online)" : ""}</span>
          <span className={styles.meta}><Icon icon={Clock} size={15} decorative /> {event.time}</span>
          {event.is_free && (
            <span className={styles.metaFree}><Icon icon={TagIcon} size={14} decorative /> <T de="Kostenlos" en="Free" /></span>
          )}
        </div>

        <p className={styles.desc}>{tl(loc, event.description, EVENT_COPY_EN[event.description] ?? event.description)}</p>

        {event.companies_attending?.length > 0 && (
          <div className={styles.companyRow}>
            <span className={styles.companyLabel}><T de="Aussteller" en="Exhibitors" /></span>
            {event.companies_attending.slice(0, 5).map((c, i) => (
              <span key={i} className={styles.companyChip}>{c}</span>
            ))}
            {event.companies_attending.length > 5 && (
              <span className={styles.companyMore}>+{event.companies_attending.length - 5}</span>
            )}
          </div>
        )}

        <div className={styles.audienceRow}>
          {event.target_audience?.slice(0, 3).map((a, i) => (
            <span key={i} className={styles.audiencePill}>{a}</span>
          ))}
          {event.format && <span className={styles.formatPill}>{event.format}</span>}
        </div>
      </div>
    </div>
  );
}

/* ─── Detail Panel ────────────────────────────────────────────────────────── */

function EventDetail({ event, onClose }: { event: CareerEvent; onClose: () => void }) {
  const loc = useTextLocale();
  const cfg = TYPE_CONFIG[event.type] || TYPE_CONFIG.karrieremesse;
  const ctaHref = event.url || `https://www.google.com/search?q=${encodeURIComponent(`${event.name} ${event.organizer} ${event.date}`)}`;

  return (
    <div className={styles.detail}>
      <div className={styles.detailHead} data-tone={cfg.tone}>
        <button type="button" onClick={onClose} className={styles.detailClose} aria-label={tl(loc, "Schließen", "Close")}>
          <Icon icon={X} size={16} decorative />
        </button>
        <span className={styles.typeBadge}>
          <Icon icon={cfg.icon} size={13} decorative /> {tl(loc, cfg.label, cfg.labelEn)}
        </span>
        <h2 className={styles.detailName}>{event.name}</h2>
        <p className={styles.detailOrg}>{event.organizer}</p>
      </div>

      <div className={styles.detailBody}>
        <div className={styles.infoGrid}>
          <div className={styles.infoCell}>
            <Icon icon={Calendar} size={16} decorative className={styles.infoIcon} />
            <p className={styles.infoLabel}><T de="Datum" en="Date" /></p>
            <p className={styles.infoVal}>{event.date}</p>
          </div>
          <div className={styles.infoCell}>
            <Icon icon={Clock} size={16} decorative className={styles.infoIcon} />
            <p className={styles.infoLabel}><T de="Uhrzeit" en="Time" /></p>
            <p className={styles.infoVal}>{event.time}</p>
          </div>
          <div className={styles.infoCell}>
            <Icon icon={MapPin} size={16} decorative className={styles.infoIcon} />
            <p className={styles.infoLabel}><T de="Ort" en="Location" /></p>
            <p className={styles.infoVal}>{event.location?.venue}</p>
            <p className={styles.infoSub}>{event.location?.city}</p>
          </div>
          <div className={styles.infoCell}>
            <Icon icon={TagIcon} size={16} decorative className={styles.infoIcon} />
            <p className={styles.infoLabel}><T de="Eintritt" en="Admission" /></p>
            <p className={styles.infoVal}>{event.is_free ? <T de="Kostenlos" en="Free" /> : <T de="Kostenpflichtig" en="Paid" />}</p>
          </div>
        </div>

        <div>
          <h3 className={styles.detailH}><T de="Über das Event" en="About the event" /></h3>
          <p className={styles.detailText}>{tl(loc, event.description, EVENT_COPY_EN[event.description] ?? event.description)}</p>
        </div>

        {event.why_relevant && (
          <div className={styles.relevance}>
            <p className={styles.relevanceText}>
              <Icon icon={Star} size={16} decorative className={styles.relevanceIcon} />
              {event.why_relevant}
            </p>
          </div>
        )}

        {event.highlights?.length > 0 && (
          <div>
            <h3 className={styles.detailH}><T de="Highlights" en="Highlights" /></h3>
            <div className={styles.tagWrap}>
              {event.highlights.map((h, i) => (
                <span key={i} className={styles.highlight}>
                  <Icon icon={CheckCircle} size={12} decorative className={styles.highlightIcon} /> {h}
                </span>
              ))}
            </div>
          </div>
        )}

        {event.companies_attending?.length > 0 && (
          <div>
            <h3 className={styles.detailH}><T de="Teilnehmende Unternehmen" en="Attending companies" /></h3>
            <div className={styles.tagWrap}>
              {event.companies_attending.map((c, i) => (
                <span key={i} className={styles.companyChipLg}>{c}</span>
              ))}
            </div>
          </div>
        )}

        <div className={styles.dualGrid}>
          <div>
            <h4 className={styles.detailH4}><T de="Zielgruppe" en="Target audience" /></h4>
            <div className={styles.tagWrapSm}>
              {event.target_audience?.map((a, i) => <span key={i} className={styles.audiencePill}>{a}</span>)}
            </div>
          </div>
          <div>
            <h4 className={styles.detailH4}><T de="Branchen" en="Industries" /></h4>
            <div className={styles.tagWrapSm}>
              {event.industries?.map((ind, i) => <span key={i} className={styles.industryPill}>{ind}</span>)}
            </div>
          </div>
        </div>

        <a href={ctaHref} target="_blank" rel="noopener noreferrer" className={styles.ctaLink}>
          <T de="Mehr Infos" en="More info" /> <Icon icon={ExternalLink} size={16} decorative />
        </a>
      </div>
    </div>
  );
}

/* ─── Page ────────────────────────────────────────────────────────────────── */

export default function CareerEventsV2() {
  const loc = useTextLocale();
  const [location, setLocation] = useState("");
  const [careerLevel, setCareerLevel] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedEvents, setSavedEvents] = useState<Set<string>>(new Set());
  const [selectedEvent, setSelectedEvent] = useState<CareerEvent | null>(null);
  const [cachedEvents, setCachedEvents] = useState<CareerEvent[]>([]);
  const [showSaved, setShowSaved] = useState(false);
  const [editPlace, setEditPlace] = useState(false);

  useEffect(() => {
    // Profile prefill (safe storage wrapper; two possible keys, as in v1).
    const p = storage.getJSON<UserProfile | null>("user_profile", null) ?? storage.getJSON<UserProfile | null>(PROFILE_KEY, null);
    if (p) {
      if (p.location) setLocation(p.location);
      if (p.career_level) setCareerLevel(p.career_level);
      if (p.career_goal) setTargetRole(p.career_goal);
    }
    setSavedEvents(new Set(storage.getJSON<string[]>(SAVED_KEY, []) ?? []));
    // A saved place answers "what is near me" right away (app-tool-career-events); the
    // job search keeps its city in job_preferences.
    const place = p?.location || storage.getJSON<{ city?: string } | null>("job_preferences", null)?.city || "";
    if (place) { if (!p?.location) setLocation(place); void runSearch(place); }
    // Cached real events from DB.
    fetch(`${API}/api/career-events/cached`)
      .then((r) => (r.ok ? r.json() : []))
      .then((events) => { if (Array.isArray(events) && events.length > 0) setCachedEvents(events); })
      .catch((e) => logger.error("career_events.cached_failed", { error: e }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once on open
  }, []);

  const handleSearch = () => runSearch(location);
  async function runSearch(where: string) {
    if (!where.trim()) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/career-events/search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location: where, career_level: careerLevel || undefined, event_type: eventTypeFilter || undefined, target_role: targetRole || undefined }),
      });
      if (!r.ok) throw new Error(tl(loc, "Suche fehlgeschlagen", "Search failed"));
      setResult(await r.json());
    } catch (e) {
      logger.error("career_events.search_failed", { error: e });
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const toggleSave = (id: string) => {
    setSavedEvents((prev) => {
      const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id);
      storage.setJSON(SAVED_KEY, [...next]);
      return next;
    });
  };

  // Real events only (search and cache both read the scraped database); no samples.
  const displayResult: SearchResult = result
    ? { ...result, tips: result.tips?.length ? result.tips : EVENT_TIPS }
    : { events: cachedEvents, total_found: cachedEvents.length, search_summary: tl(loc, `${cachedEvents.length} Events aus der Datenbank`, `${cachedEvents.length} events from database`), tips: EVENT_TIPS };
  const levelOk = (ev: CareerEvent) => !careerLevel || !ev.target_audience?.length || ev.target_audience.some((a) => LEVEL_AUDIENCE[careerLevel]?.test(a));
  const filteredEvents = displayResult.events?.filter((ev) => (!eventTypeFilter || ev.type === eventTypeFilter) && levelOk(ev) && eventIsUpcoming(ev.date)) || [];
  // Phone (app-tool-career-events): next event, then this place, then the rest of DACH.
  const place = (result ? location : "").trim();
  const shown = showSaved ? filteredEvents.filter((ev) => savedEvents.has(ev.id)) : filteredEvents;
  const isLocal = (ev: CareerEvent) => !!place && (ev.location?.city || "").toLowerCase().includes(place.toLowerCase());
  const local = shown.filter(isLocal);
  const nextEvent = local[0] ?? shown[0] ?? null;
  const rest = shown.filter((ev) => ev !== nextEvent && !isLocal(ev));
  const typeLabel = (t: string) => { const c = TYPE_CONFIG[t]; return c ? tl(loc, c.label, c.labelEn) : t; };
  const row = (ev: CareerEvent) => {
    const { day, month } = formatDateShort(ev.date);
    const meta = [typeLabel(ev.type), ev.format === "Online" ? "online" : (ev.location?.venue || ev.location?.city), ev.is_free ? tl(loc, "Kostenlos", "Free") : ev.registration_required ? tl(loc, "Anmeldung nötig", "Registration needed") : ""].filter(Boolean).join(" · ");
    return (
      <div key={ev.id} className={styles.pRow} role="button" tabIndex={0} onClick={() => setSelectedEvent(ev)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedEvent(ev); } }}>
        <span className={styles.pDate}><b>{day}</b><small>{month}</small></span>
        <span className={styles.pRowText}><b>{ev.name}</b><small>{meta}</small></span>
        <button type="button" className={styles.pSave} data-saved={savedEvents.has(ev.id) || undefined} aria-pressed={savedEvents.has(ev.id)}
          aria-label={savedEvents.has(ev.id) ? tl(loc, "Gemerkt", "Saved") : tl(loc, "Merken", "Save")}
          onClick={(e) => { e.stopPropagation(); toggleSave(ev.id); }}>
          <Icon icon={Bookmark} size={20} decorative className={savedEvents.has(ev.id) ? styles.bookmarkFilled : undefined} />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.root} data-ck="v2" data-ck-fullscreen>
      <div className={styles.phone}>
        <div className={styles.pTop}>
          <button type="button" className={styles.pBack} onClick={() => window.history.back()} aria-label={tl(loc, "Zurück", "Back")}>
            <Icon icon={ChevronLeft} size={22} decorative />
          </button>
          <button type="button" className={styles.pSavedBtn} aria-pressed={showSaved} onClick={() => setShowSaved((v) => !v)}>
            {tl(loc, "Gemerkt", "Saved")} ({savedEvents.size})
          </button>
        </div>
        <p className={styles.eyebrow}><T de="Jobsuche" en="Job search" /></p>
        <h1 className={styles.title}><T de="Karriere-Events" en="Career events" /></h1>
        <p className={styles.sub}><T de="Karrieremessen, Firmenevents und Networking in deiner Nähe · DACH-Region" en="Career fairs, company events and networking near you · DACH region" /></p>
        <div className={styles.pChips}>
          <button type="button" className={styles.pChip} data-on={location.trim() ? true : undefined} onClick={() => setEditPlace((v) => !v)}>
            <Icon icon={MapPin} size={16} decorative />{location.trim() || tl(loc, "Ort wählen", "Choose place")}
          </button>
          <label className={styles.pChip} data-on={eventTypeFilter ? true : undefined}>
            <select value={eventTypeFilter} onChange={(e) => setEventTypeFilter(e.target.value)} aria-label={tl(loc, "Event-Typ", "Event type")}>
              <option value="">{tl(loc, "Alle Typen", "All types")}</option>
              {Object.entries(TYPE_CONFIG).map(([id, c]) => <option key={id} value={id}>{tl(loc, c.label, c.labelEn)}</option>)}
            </select>
          </label>
          <label className={styles.pChip} data-on={careerLevel ? true : undefined}>
            <select value={careerLevel} onChange={(e) => setCareerLevel(e.target.value)} aria-label={tl(loc, "Karrierestufe", "Career level")}>
              <option value="">{tl(loc, "Alle Stufen", "All levels")}</option>
              <option value="pupil">{tl(loc, "Schüler/in", "Pupil")}</option>
              <option value="student">{tl(loc, "Student/in", "Student")}</option>
              <option value="entry">{tl(loc, "Berufseinstieg", "Career starter")}</option>
              <option value="professional">{tl(loc, "Berufserfahren", "Experienced")}</option>
            </select>
          </label>
        </div>
        {editPlace && (
          <form className={styles.pPlace} onSubmit={(e) => { e.preventDefault(); setEditPlace(false); void runSearch(location); }}>
            <Input label={tl(loc, "Ort", "Place")} value={location} onChange={(e) => setLocation(e.target.value)} placeholder={tl(loc, "Stadt oder PLZ", "City or postcode")} autoFocus />
          </form>
        )}
        {error && <div className={styles.errorBanner} role="alert">{error}</div>}
        {nextEvent ? (
          <>
            <p className={styles.pSec}><span className={styles.pSecIco}><Icon icon={Star} size={16} decorative /></span>{tl(loc, "Dein nächstes Event", "Your next event")}</p>
            {(() => {
              const ev = nextEvent; const { day, month } = formatDateShort(ev.date); const u = daysUntil(ev.date);
              const when = u ? (u.days === 0 ? tl(loc, "Heute", "Today") : u.days === 1 ? tl(loc, "Morgen", "Tomorrow") : tl(loc, `In ${u.days} Tagen`, `In ${u.days} days`)) + ` · ${u.weekday}` : "";
              const c = ev.companies_attending || [];
              return (
                <div className={styles.pHero} role="button" tabIndex={0} onClick={() => setSelectedEvent(ev)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedEvent(ev); } }}>
                  <div className={styles.pHeroTop}>
                    <span className={styles.pHeroDate}><b>{day}</b><small>{month}</small></span>
                    <span className={styles.pRowText}>
                      {when && <small className={styles.pWhen}>{when}</small>}
                      <b className={styles.pHeroName}>{ev.name}</b>
                      <small>{[ev.location?.venue || ev.location?.city, ev.time].filter(Boolean).join(" · ")}</small>
                    </span>
                  </div>
                  <div className={styles.pTags}>
                    <span className={styles.pTag}>{typeLabel(ev.type)}</span>
                    {ev.is_free && <span className={styles.pTag}>{tl(loc, "Kostenlos", "Free")}</span>}
                  </div>
                  {c.length > 0 && <p className={styles.pExh}><b>{tl(loc, "Aussteller:", "Exhibitors:")}</b> {c.slice(0, 3).join(", ")}{c.length > 3 ? tl(loc, ` und ${c.length - 3} weitere`, ` and ${c.length - 3} more`) : ""}</p>}
                </div>
              );
            })()}
            {local.filter((ev) => ev !== nextEvent).length > 0 && (
              <>
                <p className={styles.pSec}><span className={styles.pSecIco}><Icon icon={Calendar} size={16} decorative /></span>{tl(loc, `${local.length} Karriere-Events in ${place}`, `${local.length} career events in ${place}`)}</p>
                <div className={styles.pList}>{local.filter((ev) => ev !== nextEvent).map(row)}</div>
              </>
            )}
            {rest.length > 0 && (
              <>
                <p className={styles.pSec}><span className={styles.pSecIco}><Icon icon={MapPin} size={16} decorative /></span>{place ? tl(loc, `${rest.length} weitere im DACH-Raum`, `${rest.length} more in the DACH region`) : tl(loc, `${rest.length} Karriere-Events im DACH-Raum`, `${rest.length} career events in the DACH region`)}</p>
                <div className={styles.pList}>{rest.map(row)}</div>
              </>
            )}
          </>
        ) : (
          <EmptyState variant="no-results" icon={<Icon icon={Calendar} size={28} decorative />}
            title={showSaved ? tl(loc, "Noch nichts gemerkt", "Nothing saved yet") : tl(loc, "Keine Events gefunden", "No events found")}
            description={showSaved ? tl(loc, "Tippe bei einem Event auf das Lesezeichen.", "Tap the bookmark on an event.") : tl(loc, "Gerade sind keine bestätigten Termine mit diesen Filtern in unserer Datenbank. Wir zeigen nur echte Events mit Quelle.", "There are no confirmed dates with these filters in our database right now. We only show real events with a source.")}
            action={<Button variant="secondary" onClick={() => { setEventTypeFilter(""); setCareerLevel(""); setShowSaved(false); }}><T de="Filter zurücksetzen" en="Reset filters" /></Button>} />
        )}
        <div className={styles.pBar}>
          <Button variant="secondary" onClick={() => { setEventTypeFilter(""); setCareerLevel(""); setShowSaved(false); }}><T de="Zurücksetzen" en="Reset" /></Button>
          <Button variant="primary" onClick={() => { if (location.trim()) void runSearch(location); else setEditPlace(true); }} loading={loading} leftIcon={<Icon icon={Search} size={18} decorative />}><T de="Suchen" en="Search" /></Button>
        </div>
      </div>

      <div className={styles.desk}>
      {/* Topbar */}
      <header className={styles.topbar}>
        <p className={styles.eyebrow}><T de="Jobsuche" en="Job search" /></p>
        <h1 className={styles.title}><T de="Karriere-Events" en="Career events" /></h1>
        <p className={styles.sub}>
          <T de="Entdecke Karrieremessen, Firmenevents und Networking-Möglichkeiten in deiner Nähe" en="Discover career fairs, company events and networking opportunities near you" />
        </p>
        <div className={styles.stats}>
          <span className={styles.stat}><Icon icon={Calendar} size={15} decorative /> {filteredEvents.length} <T de="Events" en="events" /></span>
          <span className={styles.stat}><Icon icon={MapPin} size={15} decorative /> <T de="DACH-Region" en="DACH region" /></span>
          <span className={styles.stat}><Icon icon={Bookmark} size={15} decorative /> {savedEvents.size} <T de="gemerkt" en="saved" /></span>
        </div>
      </header>

      {/* Search */}
      <section className={styles.searchCard}>
        <div className={styles.searchGrid}>
          <Input
            label={tl(loc, "Ort", "Location")}
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={tl(loc, "Stadt oder PLZ", "City or postcode")}
          />
          <Select
            label={tl(loc, "Event-Typ", "Event type")}
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            options={[
              { value: "", label: tl(loc, "Alle Event-Typen", "All event types") },
              { value: "karrieremesse", label: tl(loc, "Karrieremesse", "Career fair") },
              { value: "firmenevent", label: tl(loc, "Firmenevent", "Company event") },
              { value: "networking", label: "Networking" },
              { value: "webinar", label: tl(loc, "Webinar / Online", "Webinar / Online") },
              { value: "workshop", label: "Workshop" },
              { value: "hochschulmesse", label: tl(loc, "Hochschulmesse", "University fair") },
            ]}
          />
          <Select
            label={tl(loc, "Karrierestufe", "Career level")}
            value={careerLevel}
            onChange={(e) => setCareerLevel(e.target.value)}
            options={[
              { value: "", label: tl(loc, "Alle Karrierestufen", "All career levels") },
              { value: "pupil", label: tl(loc, "Schüler/in", "Pupil") },
              { value: "student", label: tl(loc, "Student/in", "Student") },
              { value: "entry", label: tl(loc, "Berufseinsteiger/in", "Career starter") },
              { value: "professional", label: tl(loc, "Berufserfahren", "Experienced") },
            ]}
          />
          <div className={styles.searchBtnWrap}>
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={loading || !location.trim()}
              loading={loading}
              leftIcon={<Icon icon={Search} size={16} decorative />}
            >
              <T de="Suchen" en="Search" />
            </Button>
          </div>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner} role="alert">{error}</div>
      )}

      {/* Results header */}
      <div className={styles.resultsHead}>
        <div>
          <h2 className={styles.resultsTitle}>{filteredEvents.length} <T de="Karriere-Events" en="career events" /></h2>
          <p className={styles.resultsSub}>
            {displayResult.search_summary}
          </p>
        </div>
        <div className={styles.filterPills}>
          {Object.entries(TYPE_CONFIG).map(([id, cfg]) => {
            const count = displayResult.events?.filter((e) => e.type === id).length || 0;
            if (count === 0) return null;
            const active = eventTypeFilter === id;
            return (
              <button
                key={id}
                type="button"
                onClick={() => setEventTypeFilter(active ? "" : id)}
                className={styles.filterPill}
                data-active={active || undefined}
                aria-pressed={active}
              >
                <Icon icon={cfg.icon} size={13} decorative /> {tl(loc, cfg.label, cfg.labelEn)} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Cards + detail */}
      {filteredEvents.length === 0 ? (
        <EmptyState
          variant="no-results"
          icon={<Icon icon={Calendar} size={28} decorative />}
          title={tl(loc, "Keine Events gefunden", "No events found")}
          description={eventTypeFilter
            ? tl(loc, "Passe den Filter an, um mehr Events zu sehen.", "Adjust the filter to see more events.")
            : tl(loc, "Gerade sind keine bestätigten Termine in unserer Datenbank. Wir zeigen nur echte Events mit Quelle, keine Beispiele.", "There are no confirmed dates in our database right now. We only show real events with a source, no examples.")}
          action={
            <Button variant="secondary" onClick={() => setEventTypeFilter("")}>
              <T de="Filter zurücksetzen" en="Reset filter" />
            </Button>
          }
        />
      ) : (
        <div className={styles.contentRow}>
          <div className={styles.cardsGrid} data-detail={selectedEvent ? "open" : undefined}>
            {filteredEvents.map((ev) => (
              <EventCard
                key={ev.id}
                event={ev}
                saved={savedEvents.has(ev.id)}
                onToggleSave={() => toggleSave(ev.id)}
                onSelect={() => setSelectedEvent(selectedEvent?.id === ev.id ? null : ev)}
                isSelected={selectedEvent?.id === ev.id}
              />
            ))}
          </div>

          {selectedEvent && (
            <div className={styles.detailCol}>
              <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />
            </div>
          )}
        </div>
      )}


      {/* Mobile detail modal */}
      {selectedEvent && (
        <div className={styles.modalOverlay} onClick={() => setSelectedEvent(null)} role="presentation">
          <div className={styles.modalSheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={selectedEvent.name}>
            <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />
          </div>
        </div>
      )}

      {/* Tips */}
      {displayResult.tips?.length > 0 && (
        <section className={styles.tips}>
          <h3 className={styles.tipsTitle}>
            <Icon icon={Lightbulb} size={18} decorative className={styles.tipsIcon} /> <T de="Tipps für deinen Messebesuch" en="Tips for your visit" />
          </h3>
          <div className={styles.tipsGrid}>
            {displayResult.tips.map((tip, i) => (
              <div key={i} className={styles.tipCard}>
                <span className={styles.tipIconBox}>
                  <Icon icon={CheckCircle} size={16} decorative className={styles.tipCheck} />
                </span>
                <h4 className={styles.tipTitle}>{tl(loc, tip.title, tip.titleEn || tip.title)}</h4>
                <p className={styles.tipText}>{tl(loc, tip.text, tip.textEn || tip.text)}</p>
              </div>
            ))}
          </div>
        </section>
      )}
      </div>
    </div>
  );
}
